import { Check, ChevronDown } from 'lucide-react';
import { Select as RS } from 'radix-ui';
import type { ReactNode } from 'react';
import { cx } from '../lib/cx.js';
import type { ControlSize } from './Button.js';
import { useField } from './Field.js';
import './field.css';
import './overlay.css';
import './select.css';

export interface SelectOption {
  value: string;
  label: string;
  icon?: ReactNode;
  hint?: string;
  disabled?: boolean;
}

export interface SelectGroup {
  label: string;
  options: readonly SelectOption[];
}

export interface SelectProps {
  options: readonly (SelectOption | SelectGroup)[];
  value?: string;
  defaultValue?: string;
  onValueChange?: (value: string) => void;
  placeholder?: string;
  size?: ControlSize;
  disabled?: boolean;
  invalid?: boolean;
  /** Accessible name when not inside a <Field>. */
  'aria-label'?: string;
  className?: string;
  name?: string;
  /** Open the list on mount (an inline editor whose first step is the pick). */
  defaultOpen?: boolean;
}

function isGroup(o: SelectOption | SelectGroup): o is SelectGroup {
  return 'options' in o;
}

function Item({ option }: { option: SelectOption }) {
  return (
    <RS.Item value={option.value} disabled={option.disabled ?? false} className="vel-option">
      {option.icon ? <span className="vel-option__icon">{option.icon}</span> : null}
      <RS.ItemText>{option.label}</RS.ItemText>
      {option.hint ? <span className="vel-option__hint">{option.hint}</span> : null}
      <RS.ItemIndicator className="vel-option__check">
        <Check size={14} aria-hidden />
      </RS.ItemIndicator>
    </RS.Item>
  );
}

/** Single-choice dropdown for short, fixed lists. Use Combobox for search. */
export function Select({
  options,
  value,
  defaultValue,
  onValueChange,
  placeholder = 'Select',
  size = 'md',
  disabled,
  invalid,
  className,
  name,
  defaultOpen,
  ...aria
}: SelectProps) {
  const field = useField();
  const isInvalid = invalid ?? field?.invalid ?? false;
  return (
    <RS.Root
      {...(value !== undefined ? { value } : {})}
      {...(defaultValue !== undefined ? { defaultValue } : {})}
      {...(onValueChange ? { onValueChange } : {})}
      {...(disabled !== undefined ? { disabled } : {})}
      {...(name ? { name } : {})}
      {...(defaultOpen ? { defaultOpen } : {})}
    >
      <RS.Trigger
        id={field?.id}
        className={cx('vel-input', 'vel-select-trigger', className)}
        data-size={size}
        data-invalid={isInvalid ? '' : undefined}
        aria-invalid={isInvalid || undefined}
        aria-describedby={field?.describedBy}
        aria-label={aria['aria-label']}
      >
        <span className="vel-select-trigger__value">
          <RS.Value placeholder={placeholder} />
        </span>
        <RS.Icon className="vel-input__adorn">
          <ChevronDown size={14} aria-hidden />
        </RS.Icon>
      </RS.Trigger>
      <RS.Portal>
        <RS.Content className="vel-float vel-select-content" position="popper" sideOffset={4}>
          <RS.Viewport>
            {options.map((o, i) =>
              isGroup(o) ? (
                <RS.Group key={`g-${o.label}`}>
                  {i > 0 ? <RS.Separator className="vel-option-sep" /> : null}
                  <RS.Label className="vel-option-label">{o.label}</RS.Label>
                  {o.options.map((opt) => (
                    <Item key={opt.value} option={opt} />
                  ))}
                </RS.Group>
              ) : (
                <Item key={o.value} option={o} />
              ),
            )}
          </RS.Viewport>
        </RS.Content>
      </RS.Portal>
    </RS.Root>
  );
}
